import { useEffect, useRef, useState } from 'react';
import { Slider } from '@mui/material';
import { CLIENT_PURPLE } from './SupplierScoring.calc';

interface RatingSliderProps {
  label: string;
  // Undefined when unrated, which is never the same as 0.
  rating?: number;
  // Set for a function clients scored: its handle rests there while unrated, and a purple tick marks it.
  clientAverage?: number;
  // Saves the rating; for a function no client scored, on every change.
  onRate: (rating: number) => void;
  // For a function clients scored, called once on release instead, which may ask for a note before saving.
  onRelease?: (rating: number) => void;
  disabled?: boolean;
  // A change drops the value dragged but not saved, as when the note it needed is cancelled.
  resetToken?: number;
}

export const RATING_STEP = 5;
const STEPS = Array.from({ length: 100 / RATING_STEP + 1 }, (_, index) => index * RATING_STEP);

// The steps, with the client average and the value shown where either falls between them, in order.
const stopsWith = (...values: number[]) => [...new Set([...STEPS, ...values])].sort((a, b) => a - b);

const nearestStop = (stops: number[], value: number) =>
  stops.reduce((best, stop) => (Math.abs(stop - value) < Math.abs(best - value) ? stop : best));

// The range input's own keys, which at 0 change nothing and so fire no change or commit.
const KEYS_THAT_PICK_ZERO = ['Home', 'ArrowLeft', 'ArrowDown'];

const ARROWS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

// With stops in place of a step, MUI moves nothing on the Page keys, and the range input's Home and End stop one stop
// short, so these are moved here.
const KEY_JUMPS: Record<string, (value: number) => number> = {
  Home: () => 0,
  End: () => 100,
  PageDown: (value) => value - 2 * RATING_STEP,
  PageUp: (value) => value + 2 * RATING_STEP
};

const clientTick = (index: number) => ({
  '& .MuiSlider-mark': { display: 'none' },
  [`& .MuiSlider-mark[data-index="${index}"]`]: {
    display: 'block',
    backgroundColor: CLIENT_PURPLE,
    height: 12,
    width: 2,
    opacity: 1
  }
});

/** A 0–100 rating of one function in steps of 5; an untouched slider records nothing. */
export const RatingSlider = ({
  label,
  rating,
  clientAverage,
  onRate,
  onRelease,
  disabled = false,
  resetToken = 0
}: RatingSliderProps) => {
  const [dragged, setDragged] = useState<number | null>(null);
  // The value last handed to onRelease, so leaving the slider once its note dialog opens doesn't hand it over again.
  const released = useRef<number | null>(null);
  useEffect(() => {
    setDragged(null);
    released.current = null;
  }, [rating, resetToken]);
  const rated = rating !== undefined;
  const unratedLook = { color: 'grey.400' };

  // The client average is a stop of its own, so the slider can land on it, where no note is needed.
  if (clientAverage !== undefined && onRelease) {
    const shown = dragged ?? rating ?? clientAverage;
    const stops = stopsWith(clientAverage, shown);
    const release = (value: number | null) => {
      if (value === null || value === released.current) return;
      released.current = value;
      onRelease(value);
    };
    return (
      <Slider
        value={shown}
        min={0}
        max={100}
        step={null}
        size="small"
        disabled={disabled}
        track={rated ? 'normal' : false}
        marks={stops.map((value) => ({ value }))}
        onChange={(_, value) => setDragged(value as number)}
        // A pointer lets go once; the keyboard commits on every key through the range input's change event, so its
        // value waits for Enter or for focus to leave the slider, and a note is asked for once.
        onChangeCommitted={(event, value) => {
          if (event.type !== 'change') release(value as number);
        }}
        onKeyDown={(event) => {
          const jump = KEY_JUMPS[event.key];
          if (jump) {
            event.preventDefault();
            setDragged(nearestStop(stops, jump(shown)));
          }
          // An unrated handle rests at the client average, where an arrow at the end of the range moves nothing. Runs
          // after the slider's own key handling, so it keeps any value that key moved to.
          if (!rated && ARROWS.includes(event.key)) setDragged((current) => current ?? clientAverage);
          if (event.key === 'Enter') release(dragged);
        }}
        onBlur={() => release(dragged)}
        slotProps={{ input: { 'aria-label': `Rate ${label}` } }}
        sx={
          rated
            ? clientTick(stops.indexOf(clientAverage))
            : { ...clientTick(stops.indexOf(clientAverage)), ...unratedLook }
        }
      />
    );
  }

  return (
    <Slider
      value={rating ?? 0}
      min={0}
      max={100}
      step={RATING_STEP}
      size="small"
      disabled={disabled}
      color={rated ? 'primary' : 'secondary'}
      track={rated ? 'normal' : false}
      onChange={(_, value) => onRate(value as number)}
      // An unrated slider already sits at 0, so picking 0 fires no change; only the commit says it was chosen.
      onChangeCommitted={rated ? undefined : (_, value) => onRate(value as number)}
      onKeyDown={
        rated
          ? undefined
          : (event) => {
              if (KEYS_THAT_PICK_ZERO.includes(event.key)) onRate(0);
            }
      }
      slotProps={{ input: { 'aria-label': `Rate ${label}` } }}
      sx={rated ? undefined : unratedLook}
    />
  );
};
